import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import {
    X,
    Minus,
    Maximize2,
    Phone,
    PhoneOff,
    ExternalLink,
    Loader2,
    AlertCircle,
    CheckCircle2
} from 'lucide-react';

import { getAccessToken } from '../../shared/utils/secureTokenStorage';

// Always use production API for SAML auth (IAM is configured for production)
const SAML_API_BASE = 'https://api.cloudmortgage.io';

// Reference to the CCP popup window (module-level for persistence)
let ccpPopupWindow = null;

/**
 * PhoneCallModal - Opens Connect CCP in a popup window with SAML SSO
 *
 * Since AWS Connect SAML instances block iframe embedding (frame-ancestors 'none'),
 * we use a popup window approach instead. This provides:
 * - Seamless SAML authentication (no credentials needed)
 * - Full CCP functionality in the popup
 * - Parent window can track popup status
 *
 * IMPORTANT: During SAML flow, the popup navigates through multiple domains:
 * cloudmortgage.io -> signin.aws.amazon.com -> console.aws.amazon.com -> my.connect.aws
 * Cross-origin restrictions prevent reliable monitoring of popup.closed during this flow.
 */
function PhoneCallModal({
    isOpen,
    isMinimized,
    onClose,
    onToggleMinimize,
    callStatus,
    currentCall,
    callDuration,
    onCCPReady,
    setCallStatus,
    agentPhoneNumber = '(469) 750-3330'
}) {
    const nodeRef = useRef(null);
    const [position, setPosition] = useState(() => ({
        x: typeof window !== 'undefined' ? window.innerWidth - 400 : 0,
        y: 20
    }));

    const [popupStatus, setPopupStatus] = useState('closed'); // 'closed' | 'opening' | 'open' | 'error'
    const [errorMessage, setErrorMessage] = useState(null);

    // Track if we've already attempted to open - prevents infinite reopen loop during SAML
    const [hasAttemptedOpen, setHasAttemptedOpen] = useState(false);
    // Track when SAML flow started - for grace period during cross-origin navigation
    const [samlStartTime, setSamlStartTime] = useState(null);

    /**
     * Open CCP in a new window/tab with SAML authentication
     *
     * Uses a new tab (not restricted popup) for better cross-domain cookie handling.
     * This is necessary because AWS SAML SSO sets cookies on .amazon.com but
     * Connect CCP is on .my.connect.aws - popups have stricter cookie policies.
     */
    const openCCPPopup = useCallback(() => {
        // If popup is already open, just focus it
        if (ccpPopupWindow) {
            try {
                if (!ccpPopupWindow.closed) {
                    ccpPopupWindow.focus();
                    setPopupStatus('open');
                    return;
                }
            } catch (e) {
                // Cross-origin - can't check closed status, assume it's still alive
                console.log('[PhoneDialer] Cannot check popup status (cross-origin), focusing');
                ccpPopupWindow.focus();
                setPopupStatus('open');
                return;
            }
        }

        const token = getAccessToken();
        if (!token) {
            setErrorMessage('Not authenticated. Please log in again.');
            setPopupStatus('error');
            return;
        }

        setPopupStatus('opening');
        setErrorMessage(null);
        setHasAttemptedOpen(true);
        setSamlStartTime(Date.now());

        const popupUrl = `${SAML_API_BASE}/api/connect/saml-auto-login?token=${encodeURIComponent(token)}`;
        console.log('[PhoneDialer] Opening SAML auto-login in new window');

        // Open as a new window (not a restricted popup) for better SSO cookie handling
        // The key difference: we use 'noopener' but NOT 'noreferrer', and we allow
        // the window to have standard browser capabilities (no restrictive flags)
        ccpPopupWindow = window.open(
            popupUrl,
            'CloudMortgage_Phone',
            'width=420,height=700,resizable=yes,scrollbars=yes'
        );

        if (!ccpPopupWindow) {
            setErrorMessage('Window was blocked. Please allow popups for this site.');
            setPopupStatus('error');
            setHasAttemptedOpen(false);
            return;
        }

        setPopupStatus('open');

        // Monitor popup status with cross-origin safety
        const checkPopup = setInterval(() => {
            // Check if popup reference exists
            if (!ccpPopupWindow) {
                clearInterval(checkPopup);
                setPopupStatus('closed');
                console.log('[PhoneDialer] CCP window reference lost');
                return;
            }

            // Try to check if popup is closed - may fail for cross-origin
            try {
                // During SAML flow (first 45 seconds), be lenient about "closed" status
                // because cross-origin navigation can make .closed unreliable
                const timeSinceStart = samlStartTime ? Date.now() - samlStartTime : 0;
                const inSamlFlow = timeSinceStart < 45000;  // 45 second grace period

                if (ccpPopupWindow.closed) {
                    if (inSamlFlow) {
                        // During SAML, popup might appear "closed" due to cross-origin
                        // but it's actually navigating. Don't clear yet.
                        console.log('[PhoneDialer] Popup appears closed during SAML flow, waiting...');
                    } else {
                        // After SAML grace period, trust the closed status
                        clearInterval(checkPopup);
                        ccpPopupWindow = null;
                        setPopupStatus('closed');
                        setHasAttemptedOpen(false);  // Allow reopening on next button click
                        console.log('[PhoneDialer] CCP window closed');
                    }
                }
            } catch (e) {
                // SecurityError: can't access cross-origin popup properties
                // This is expected during SAML flow - popup is still alive
                console.log('[PhoneDialer] Cross-origin popup, cannot check status (this is normal)');
            }
        }, 2000);  // Check every 2 seconds instead of 1

        // Notify that CCP is ready (for click-to-call functionality)
        if (onCCPReady) {
            setTimeout(() => onCCPReady(), 5000);  // Increased delay for SAML flow
        }
    }, [onCCPReady, samlStartTime]);

    /**
     * Focus the existing popup or open a new one
     */
    const focusOrOpenPopup = useCallback(() => {
        if (ccpPopupWindow) {
            try {
                if (!ccpPopupWindow.closed) {
                    ccpPopupWindow.focus();
                    setPopupStatus('open');
                    return;
                }
            } catch (e) {
                // Cross-origin, try to focus anyway
                ccpPopupWindow.focus();
                setPopupStatus('open');
                return;
            }
        }
        // No popup or it's closed, open a new one
        setHasAttemptedOpen(false);  // Reset so auto-open can work
        openCCPPopup();
    }, [openCCPPopup]);

    /**
     * Close the popup
     */
    const closePopup = useCallback(() => {
        if (ccpPopupWindow) {
            try {
                if (!ccpPopupWindow.closed) {
                    ccpPopupWindow.close();
                }
            } catch (e) {
                // Cross-origin, can't close
                console.log('[PhoneDialer] Cannot close cross-origin popup');
            }
        }
        ccpPopupWindow = null;
        setPopupStatus('closed');
        setHasAttemptedOpen(false);
    }, []);

    // Check popup status on mount and when modal opens
    useEffect(() => {
        if (isOpen) {
            if (ccpPopupWindow) {
                try {
                    if (!ccpPopupWindow.closed) {
                        setPopupStatus('open');
                    } else {
                        setPopupStatus('closed');
                        ccpPopupWindow = null;
                    }
                } catch (e) {
                    // Cross-origin - assume open
                    setPopupStatus('open');
                }
            } else {
                setPopupStatus('closed');
            }
        }
    }, [isOpen]);

    // Auto-open popup when modal opens - ONLY if we haven't already tried
    useEffect(() => {
        if (isOpen && !isMinimized && popupStatus === 'closed' && !hasAttemptedOpen) {
            // Small delay to let modal render first
            const timer = setTimeout(() => {
                openCCPPopup();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isOpen, isMinimized, popupStatus, openCCPPopup, hasAttemptedOpen]);

    // Reset hasAttemptedOpen when modal closes
    useEffect(() => {
        if (!isOpen) {
            setHasAttemptedOpen(false);
            setSamlStartTime(null);
        }
    }, [isOpen]);

    // Listen for postMessage from SAML page
    useEffect(() => {
        const handleMessage = (event) => {
            // Accept messages from our API domain
            if (!event.origin.includes('cloudmortgage.io') &&
                !event.origin.includes('localhost')) {
                return;
            }

            if (event.data && event.data.type) {
                if (event.data.type === 'CONNECT_SAML_START') {
                    console.log('[PhoneDialer] SAML flow started');
                    setPopupStatus('opening');
                } else if (event.data.type === 'CONNECT_SAML_SUBMITTING') {
                    console.log('[PhoneDialer] SAML form submitting to AWS');
                } else if (event.data.type === 'CONNECT_SAML_ERROR') {
                    console.error('[PhoneDialer] SAML error:', event.data.error);
                    setPopupStatus('error');
                    setErrorMessage(event.data.error);
                    setHasAttemptedOpen(false);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Reset position when opening
    useEffect(() => {
        if (isOpen && !isMinimized) {
            const modalWidth = 360;
            setPosition({ x: window.innerWidth - modalWidth - 20, y: 20 });
        }
    }, [isOpen, isMinimized]);

    // Minimized view
    if (isOpen && isMinimized) {
        return (
            <Draggable nodeRef={nodeRef} handle=".drag-handle" bounds="parent">
                <div
                    ref={nodeRef}
                    className="fixed bottom-4 right-4 z-50 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
                    style={{ width: '280px' }}
                >
                    <div className="drag-handle flex items-center justify-between p-3 bg-slate-800/50 cursor-move">
                        <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${popupStatus === 'open' ? 'bg-green-500' : 'bg-slate-700'}`}>
                                <Phone className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-white">Phone</p>
                                <p className="text-xs text-slate-400">
                                    {popupStatus === 'open' ? 'Connected' : 'Click to open'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={onToggleMinimize}
                                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                            >
                                <Maximize2 className="w-4 h-4" />
                            </button>
                            {popupStatus === 'open' && (
                                <button
                                    onClick={focusOrOpenPopup}
                                    className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
                                    title="Focus phone window"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Draggable>
        );
    }

    // Full modal view
    return (
        <Draggable
            nodeRef={nodeRef}
            handle=".drag-handle"
            bounds="parent"
            position={position}
            onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
            disabled={!isOpen}
        >
            <div
                ref={nodeRef}
                className="fixed z-[9999] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
                style={{
                    width: '360px',
                    visibility: isOpen ? 'visible' : 'hidden',
                    pointerEvents: isOpen ? 'auto' : 'none',
                    opacity: isOpen ? 1 : 0,
                }}
            >
                {/* Title bar */}
                <div className="drag-handle flex items-center justify-between px-4 py-3 bg-slate-800 cursor-move border-b border-slate-700">
                    <div className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm font-semibold text-white">Phone</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={onToggleMinimize}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                            title="Minimize"
                        >
                            <Minus className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => {
                                closePopup();
                                onClose();
                            }}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                            title="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Status indicator */}
                    <div className="flex flex-col items-center mb-6">
                        {popupStatus === 'opening' && (
                            <>
                                <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mb-3" />
                                <p className="text-slate-300 text-sm">Opening phone system...</p>
                                <p className="text-slate-500 text-xs mt-1">Authenticating with AWS...</p>
                            </>
                        )}

                        {popupStatus === 'open' && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                                </div>
                                <p className="text-emerald-400 font-medium mb-1">Phone Connected</p>
                                <p className="text-slate-400 text-sm text-center">
                                    The phone dialer is open in a separate window.
                                </p>
                            </>
                        )}

                        {popupStatus === 'closed' && !errorMessage && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center mb-3">
                                    <Phone className="w-8 h-8 text-slate-400" />
                                </div>
                                <p className="text-slate-300 font-medium mb-1">Phone System</p>
                                <p className="text-slate-400 text-sm text-center">
                                    Click below to open the phone dialer.
                                </p>
                            </>
                        )}

                        {popupStatus === 'error' && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-3">
                                    <AlertCircle className="w-10 h-10 text-red-400" />
                                </div>
                                <p className="text-red-400 font-medium mb-1">Connection Error</p>
                                <p className="text-slate-400 text-sm text-center">
                                    {errorMessage || 'Failed to open phone system.'}
                                </p>
                            </>
                        )}
                    </div>

                    {/* Action button */}
                    {popupStatus === 'open' ? (
                        <button
                            onClick={focusOrOpenPopup}
                            className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <ExternalLink className="w-5 h-5" />
                            Focus Phone Window
                        </button>
                    ) : popupStatus === 'opening' ? (
                        <button
                            disabled
                            className="w-full py-3 px-4 bg-slate-700 text-slate-400 font-medium rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Opening...
                        </button>
                    ) : (
                        <button
                            onClick={() => {
                                setHasAttemptedOpen(false);
                                openCCPPopup();
                            }}
                            className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <Phone className="w-5 h-5" />
                            Open Phone Dialer
                        </button>
                    )}

                    {/* Help text */}
                    <p className="text-xs text-slate-500 text-center mt-4">
                        {popupStatus === 'open'
                            ? 'Use the popup window to make and receive calls.'
                            : 'The phone will open in a new window for best experience.'}
                    </p>

                    {/* Agent phone number */}
                    <div className="mt-6 pt-4 border-t border-slate-700">
                        <p className="text-xs text-slate-500 text-center">
                            Your phone number: <span className="text-slate-400">{agentPhoneNumber}</span>
                        </p>
                    </div>
                </div>
            </div>
        </Draggable>
    );
}

PhoneCallModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isMinimized: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onToggleMinimize: PropTypes.func.isRequired,
    callStatus: PropTypes.string,
    currentCall: PropTypes.object,
    callDuration: PropTypes.number,
    onCCPReady: PropTypes.func,
    setCallStatus: PropTypes.func,
    agentPhoneNumber: PropTypes.string
};

PhoneCallModal.defaultProps = {
    isMinimized: false,
    callDuration: 0
};

export default PhoneCallModal;
